import React, { forwardRef, useImperativeHandle, useRef } from 'react';
import { createPortal } from 'react-dom';
import DragResizeBox from '../../../components/DragResizeBox/DragResizeBox';
import {
  COPILOT_DOCK_PORTAL_ID,
  COPILOT_HOME_DOCK_PORTAL_ID,
} from '@/pages/aigc/pages/Agent/config/constant';
import {
  COPILOT_PANEL_DEFAULT_TOP,
  COPILOT_PANEL_MIN_WIDTH,
  getCopilotPanelInitialWidth,
  getCopilotPanelMaxWidth,
} from '../../copilotPanelLayout';
import type { TDragResizeBoxRef } from '../../types/window';

/** 浮窗距视口边缘安全距离（与 release Copilot 一致） */
const SCREEN_MARGIN = 15;
/** 顶栏高度，用于计算面板最大/初始高度 */
const NAV_BAR_HEIGHT = 48;

/**
 * 浮窗尺寸约束（随 window.innerHeight 变化）。
 */
const getWindowSizeConstraints = () => ({
  maxWidth: getCopilotPanelMaxWidth(),
  minHeight: window.innerHeight * 0.5,
  maxHeight: window.innerHeight - NAV_BAR_HEIGHT,
  initialHeight: window.innerHeight - NAV_BAR_HEIGHT,
});

/**
 * 内嵌 Portal 挂载点：Home 专用槽优先，否则全局右侧槽，最后 fallback body。
 */
function getDockPortalTarget(): HTMLElement {
  return (
    document.getElementById(COPILOT_HOME_DOCK_PORTAL_ID)
    || document.getElementById(COPILOT_DOCK_PORTAL_ID)
    || document.body
  );
}

/** {@link CopilotPanelShell} 组件 Props */
export interface ICopilotPanelShellProps {
  /** 是否内嵌停靠（否则为可拖拽悬浮面板） */
  isDocked: boolean;
  /** 悬浮时面板 z-index，用于与报告窗层级切换 */
  zIndex: number;
  /** 开始拖拽/缩放时回调（用于聚焦 Copilot） */
  onResizeStart?: () => void;
  children: React.ReactNode;
}

/**
 * Copilot 主会话面板外壳：内嵌时 Portal 到右侧槽位，悬浮时为 DragResizeBox。
 * 与 CopilotReportWindow（报告浮窗）职责分离。
 */
const CopilotPanelShell = forwardRef<TDragResizeBoxRef, ICopilotPanelShellProps>(
  ({ isDocked, zIndex, onResizeStart, children }, ref) => {
    const dockedContainerRef = useRef<HTMLDivElement>(null);

    /**
     * 内嵌无 DragResizeBox，暴露 dropAreaRef 供 QAInput 拖拽上传；
     * 悬浮时由子组件 DragResizeBox 的 useImperativeHandle 覆盖。
     */
    useImperativeHandle(
      ref,
      () => ({
        dropAreaRef: dockedContainerRef,
        handleMouseDown: () => {},
      }),
      [],
    );

    if (isDocked) {
      return createPortal(
        <div
          ref={dockedContainerRef}
          className="dbh-agent-copilot dbh-agent-copilot--docked"
        >
          {children}
        </div>,
        getDockPortalTarget(),
      );
    }

    const { maxWidth, minHeight, maxHeight, initialHeight } =
      getWindowSizeConstraints();
    const panelInitialWidth = getCopilotPanelInitialWidth();

    return (
      <DragResizeBox
        defaultWidth={panelInitialWidth}
        defaultHeight={initialHeight}
        defaultX={window.innerWidth - panelInitialWidth - SCREEN_MARGIN}
        defaultY={COPILOT_PANEL_DEFAULT_TOP}
        minWidth={COPILOT_PANEL_MIN_WIDTH}
        minHeight={minHeight}
        maxWidth={maxWidth}
        maxHeight={maxHeight}
        ref={ref}
        className="dbh-agent-copilot"
        safeArea={{
          top: COPILOT_PANEL_DEFAULT_TOP,
          right: SCREEN_MARGIN,
          bottom: SCREEN_MARGIN,
          left: SCREEN_MARGIN,
        }}
        contentStyle={{
          height: 'calc(100% - 140px)',
          overflow: 'hidden',
        }}
        style={{ zIndex }}
        onResizeStart={onResizeStart}
      >
        {children}
      </DragResizeBox>
    );
  },
);

CopilotPanelShell.displayName = 'CopilotPanelShell';

export default CopilotPanelShell;
